import { Button, Group, Select } from '@mantine/core';
import {
  IconAlertTriangle,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconSchool,
  IconUsers,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { browserPdfOutput, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import table from '../pms/Pms.module.css';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { downloadFile, XLSX_TYPE } from '../reports/xlsx.js';
import { competencyFileName, competencyPdf, competencyWorkbook } from './competency-export.js';
import { drillRecordPath } from './DrillFindings.js';
import classes from './DrillPlanner.module.css';
import type { DrillCompetency, DrillCompetencySource } from './finding-types.js';

export interface DrillCompetencyScreenProps {
  source: DrillCompetencySource;
}

/**
 * Operations ▸ Drills ▸ Crew Competency (belge §6; ADR 0128 Faz 4 Ek): kişi × tatbikat türü;
 * son katılım, rol ve değerlendirme kaptan onaylı kayıtlardan. Yangın ve gemiyi terk tatbikatı
 * her mürettebat için aylık zorunlu (SOLAS III/19.3.2); eksik olan işaretlenir.
 */
export function DrillCompetencyScreen({ source }: DrillCompetencyScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const vesselId = source.scope === 'office' ? query.get('vessel') : null;
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: DrillCompetency }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.load(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const data = state.kind === 'ready' ? state.data : null;
  const exportMatrix = async (format: 'pdf' | 'xlsx') => {
    if (!data) return;
    setExporting(format);
    setExportError(null);
    try {
      const tx = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
      const name = competencyFileName(data, format);
      if (format === 'pdf') {
        const definition = competencyPdf(data, tx, new Date().toISOString());
        await (source.pdf ?? browserPdfOutput()).save(name, () => renderPdf(definition));
      } else {
        const bytes = await competencyWorkbook(data, tx);
        if (source.saveFile) await source.saveFile(name, bytes, XLSX_TYPE);
        else downloadFile(name, bytes, XLSX_TYPE);
      }
    } catch {
      setExportError(t('drillReport.archive.failed'));
    } finally {
      setExporting(null);
    }
  };
  const header = (
    <PageHeader
      title={t('menu.operationsDrillsCompetency')}
      description={t('drillFinding.competency.description')}
      actions={
        data && data.matrix.rows.length > 0 ? (
          <Group gap={8}>
            <Button
              variant="default"
              leftSection={<IconFileTypePdf size={16} aria-hidden />}
              loading={exporting === 'pdf'}
              disabled={exporting !== null}
              onClick={() => void exportMatrix('pdf')}
            >
              {t('drillReport.competency.pdf')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconFileSpreadsheet size={16} aria-hidden />}
              loading={exporting === 'xlsx'}
              disabled={exporting !== null}
              onClick={() => void exportMatrix('xlsx')}
            >
              {t('drillReport.competency.excel')}
            </Button>
          </Group>
        ) : undefined
      }
    />
  );
  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={6} label={t('menu.operationsDrillsCompetency')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('drillFinding.competency.error')}
            onRetry={() => void load()}
          />
        </Panel>
      </div>
    );
  }
  return (
    <div className={table.screen}>
      {header}
      {exportError ? (
        <p role="alert" className={classes.muted}>
          {exportError}
        </p>
      ) : null}
      <CompetencyBody data={state.data} onVessel={(id) => setQuery({ vessel: id })} />
    </div>
  );
}

/** Yüklenmiş matris (ekran testleri doğrudan çizer). */
export function CompetencyBody({
  data,
  onVessel,
}: {
  data: DrillCompetency;
  onVessel?: (vesselId: string | null) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillFinding.competency.${key}`, values);
  const shell = useShell();
  const { matrix } = data;
  const office = data.side === 'office';
  const rating = (value: string | null) =>
    value === 'satisfactory'
      ? t('drillRecord.evaluation.satisfactory')
      : value === 'needs_improvement'
        ? t('drillRecord.evaluation.needsImprovement')
        : null;
  return (
    <>
      {office && data.vessels.length > 1 ? (
        <Panel>
          <Select
            label={k('vessel')}
            data={data.vessels.map((v) => ({ value: v.id, label: v.name }))}
            value={data.vesselId}
            allowDeselect={false}
            onChange={(value) => onVessel?.(value)}
            maw={320}
          />
        </Panel>
      ) : null}
      <div className={classes.tiles} data-count="3">
        <StatTile
          icon={<IconUsers size={20} />}
          label={k('people')}
          value={matrix.summary.people}
          wrapLabel
        />
        <StatTile
          icon={<IconAlertTriangle size={20} />}
          label={k('peopleMissing')}
          value={matrix.summary.missing}
          tone={matrix.summary.missing > 0 ? 'danger' : 'success'}
          wrapLabel
        />
        <StatTile
          icon={<IconSchool size={20} />}
          label={k('openNeeds')}
          value={matrix.summary.openTrainingNeeds}
          tone={matrix.summary.openTrainingNeeds > 0 ? 'warning' : 'default'}
          wrapLabel
        />
      </div>
      <Panel>
        {matrix.rows.length === 0 ? (
          <EmptyState icon={<IconUsers size={22} />} title={k('empty')} />
        ) : matrix.drills.length === 0 ? (
          <EmptyState icon={<IconUsers size={22} />} title={k('noDrills')} />
        ) : (
          <div className={table.tableBox}>
            <table className={table.table} data-cards data-testid="competency-matrix">
              <thead>
                <tr>
                  <th scope="col">{k('person')}</th>
                  {matrix.drills.map((d) => (
                    <th scope="col" key={d.templateId} title={d.name}>
                      {d.code}
                      {matrix.rows.some((r) => r.cells[d.templateId]?.mandatory) ? ' *' : ''}
                    </th>
                  ))}
                  <th scope="col">{k('lastDrill')}</th>
                  <th scope="col">{k('trainingNeeds')}</th>
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr key={row.userId}>
                    <td data-label={k('person')}>
                      <strong>{row.name}</strong>
                      {row.rank ? <div className={classes.muted}>{row.rank}</div> : null}
                    </td>
                    {matrix.drills.map((d) => {
                      const cell = row.cells[d.templateId];
                      const label = `${d.code} · ${d.name}`;
                      if (!cell) return <td key={d.templateId} data-label={label} />;
                      return (
                        <td key={d.templateId} data-label={label}>
                          {cell.lastOn ? (
                            <div>
                              {cell.recordId ? (
                                <button
                                  type="button"
                                  className={classes.linkButton}
                                  onClick={() =>
                                    shell?.navigate(
                                      drillRecordPath(
                                        cell.recordId as string,
                                        office ? data.vesselId : null,
                                      ),
                                    )
                                  }
                                >
                                  {formatDay(cell.lastOn)}
                                </button>
                              ) : (
                                formatDay(cell.lastOn)
                              )}
                              <div className={classes.muted}>
                                {[cell.role, rating(cell.rating), k('times', { count: cell.count })]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            </div>
                          ) : (
                            <div className={classes.muted}>{k('notYet')}</div>
                          )}
                          {cell.missing ? (
                            <StatusBadge tone="danger">{k('missing')}</StatusBadge>
                          ) : cell.dueOn ? (
                            <div className={classes.muted}>
                              {k('due', { date: formatDay(cell.dueOn) })}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                    <td data-label={k('lastDrill')}>{row.lastOn ? formatDay(row.lastOn) : '—'}</td>
                    <td data-label={k('trainingNeeds')} data-numeric>
                      {row.openTrainingNeeds > 0 ? (
                        <button
                          type="button"
                          className={classes.linkButton}
                          onClick={() => shell?.navigate('/crew/training?status=open')}
                        >
                          {row.openTrainingNeeds}
                        </button>
                      ) : (
                        '0'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={classes.muted}>* {k('mandatory')}</p>
      </Panel>
    </>
  );
}
